import * as React from 'react';

import type {DisplayOptionEntity} from 'nutshell-core/utils';

import {BulletedList} from '../../../bulleted-list/bulleted-list';
import {CheckmarkButton} from '../../../button/checkmark-button/checkmark-button';
import {CloseButton} from '../../../button/close-button/close-button';
import {BaseRow} from '../../../row/base-row/base-row';
import {TextField} from '../../../textfield/textfield';

import {PotentialPeepsDropdown} from '../../potential-peeps-dropdown/potential-peeps-dropdown';

import type {AccountInput} from '../types';

import {FIELD_NAME, FIELD_ADDRESS, FIELD_DESCRIPTION} from './consts';
import {shouldShowPotentialAccounts, shouldAllowCreate, handleKeyboardNavigation} from './helpers';
import type {AccountFormFieldTypes} from './types';

type Props = {
    temporaryAccount?: {
        id: string;
    } & AccountInput;
    onSelectAccount: (arg1: DisplayOptionEntity) => void;
    onSubmit: (arg1: AccountInput) => void;
    onSearchPotentialAccounts?: (arg1: AccountInput) => Promise<any>;
    onClose: () => void;
    isPotentialAccountsPopover?: boolean;
};

export function AccountCardCreateable(props: Props) {
    const [name, setName] = React.useState<string>(
        props.temporaryAccount && props.temporaryAccount.name ? props.temporaryAccount.name : ''
    );
    const [description, setDescription] = React.useState<string>(
        props.temporaryAccount && props.temporaryAccount.description
            ? props.temporaryAccount.description
            : ''
    );
    const [address, setAddress] = React.useState<string>(
        props.temporaryAccount && props.temporaryAccount.address
            ? props.temporaryAccount.address
            : ''
    );
    const [potentialAccounts, setPotentialAccounts] = React.useState<DisplayOptionEntity[]>([]);
    const [formErrors, setFormErrors] = React.useState<string[]>([]);

    const nameRef = React.useRef<HTMLInputElement | null | undefined | null>(null);
    const addressRef = React.useRef<HTMLInputElement | null | undefined | null>(null);
    const descriptionRef = React.useRef<HTMLInputElement | null | undefined | null>(null);
    const popoverRef = React.useRef<HTMLElement | null | undefined | null>(null);

    const shouldShowPotentialAccountsComponent = shouldShowPotentialAccounts(
        name,
        description,
        address
    );
    const shouldAllowCreateAccount = shouldAllowCreate(
        Boolean(props.temporaryAccount),
        name,
        address
    );

    React.useEffect(() => {
        if (shouldShowPotentialAccountsComponent) {
            props.onSearchPotentialAccounts &&
                props
                    .onSearchPotentialAccounts({
                        name,
                        description,
                        address,
                    })
                    .then((matchedAccounts: DisplayOptionEntity[]) => {
                        setPotentialAccounts(matchedAccounts);
                    });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, description, address]);

    const handleSubmitAccount = () => {
        if (!name.trim() && !address.trim()) {
            setFormErrors(['At least a company name or location is required']);

            return;
        }
        setFormErrors([]);
        props.onSubmit({
            name,
            description,
            address,
        });
    };

    const handleEnter = (e: React.KeyboardEvent) => {
        e.preventDefault();
        handleSubmitAccount();
    };

    const getFieldHasError = () => {
        return formErrors.length > 0;
    };

    const handleKeyDown = (e: React.KeyboardEvent, currentField: AccountFormFieldTypes) => {
        handleKeyboardNavigation(e, currentField, addressRef, descriptionRef, nameRef);
    };

    return (
        <>
            <BaseRow getRowRef={popoverRef} type='accounts'>
                <div className='flex flex-dir-col gap-8'>
                    <div className='flex gap-8'>
                        <div className='flex flex-dir-col gap-8 full-width'>
                            <TextField
                                placeholder='Company name'
                                value={name}
                                onChange={(val) => {
                                    setName(val);
                                    setFormErrors([]);
                                }}
                                onEnter={handleEnter}
                                // @ts-expect-error
                                autoFocus={true}
                                size='small'
                                onKeyDown={(e) => handleKeyDown(e, FIELD_NAME)}
                                textFieldRef={nameRef}
                                hasError={getFieldHasError()}
                            />
                            <TextField
                                placeholder='Description'
                                value={description}
                                onEnter={handleEnter}
                                onChange={(val) => setDescription(val)}
                                size='small'
                                onKeyDown={(e) => handleKeyDown(e, FIELD_DESCRIPTION)}
                                textFieldRef={descriptionRef}
                            />
                            <TextField
                                placeholder='Location'
                                size='small'
                                value={address}
                                onEnter={handleEnter}
                                onChange={(val) => {
                                    setAddress(val);
                                    setFormErrors([]);
                                }}
                                onKeyDown={(e) => handleKeyDown(e, FIELD_ADDRESS)}
                                textFieldRef={addressRef}
                                hasError={getFieldHasError()}
                            />
                        </div>
                        <div className='flex flex-dir-col align-center gap-8'>
                            {props.temporaryAccount && (
                                <CheckmarkButton
                                    className='no-padding'
                                    // @ts-expect-error
                                    size={12}
                                    onClick={handleSubmitAccount}
                                    variant='dark'
                                />
                            )}
                            <CloseButton className='no-padding' size={12} onClick={props.onClose} />
                        </div>
                    </div>
                    {formErrors && formErrors.length > 0 && (
                        <BulletedList
                            bullets={formErrors.map((error) => error)}
                            variant='errors'
                            size='small'
                            marginBottom={8}
                        />
                    )}
                </div>
            </BaseRow>
            {shouldShowPotentialAccountsComponent && (
                <PotentialPeepsDropdown
                    potentialPeeps={potentialAccounts}
                    onSelectPeep={props.onSelectAccount}
                    popoverRef={popoverRef}
                    isPopover={props.isPotentialAccountsPopover}
                    createButtonProps={{
                        shouldShow: Boolean(shouldAllowCreateAccount),
                        onCreate: handleSubmitAccount,
                        entityName: name,
                        entityType: 'company',
                    }}
                />
            )}
        </>
    );
}
